/**
 * AgentMappingProfile — mapeo de campos y umbral de offline del agente.
 *
 * QUÉ HABÍA ANTES
 * ---------------------------------------------------------------------------
 * Un formulario de Bootstrap a pelo: `form-control form-control-sm`, `form-range`,
 * una tarjeta `card bg-light` con el bloque de Redfish, un `badge bg-secondary`,
 * un `alert alert-secondary` al final y la tabla de reglas con
 * `table table-sm` + `table-light` (que en los skins oscuros se pinta negra: ver
 * `docs/ESTANDAR_LISTADOS.md` §11). Además el botón de guardar era el único sitio
 * donde se veía el estado, y no había forma de **descartar** lo tecleado.
 *
 * AHORA
 * ---------------------------------------------------------------------------
 * Página de sección del estándar: cabecera `list-head` (embebida, porque vive
 * dentro de las pestañas de Agente CMDB) con **Guardar**, **Descartar** y
 * **Volver a las reglas de serie**; y el contenido en `Panel`s, que son la
 * tarjeta del sistema. Las reglas de mapeo dejan de ser una tabla de Bootstrap y
 * pasan a **filas de tokens**, con sus campos y su botón de quitar.
 *
 * El `profile` que devuelve el backend se guarda tal cual al cargar: «Descartar»
 * vuelve a él sin pedir nada, que es lo que se espera de un formulario largo.
 *
 * Se usa en dos sitios, y por eso tiene `embedded`:
 *  · La pestaña **Mapeo** de Agente CMDB (`embedded`, por defecto): la página ya
 *    tiene su título y sus pestañas, así que aquí va una cabecera de sección.
 *  · La ruta suelta `/admin/agent-mapping` (`embedded={false}`): es la pantalla
 *    entera. Antes esa ruta tenía su **propio componente duplicado**
 *    (`AgentMappingPanel`, 413 líneas con el mismo formulario en Bootstrap):
 *    ahora la URL sigue funcionando y sirve esta misma pantalla.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowsRightLeftIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Panel } from './common/Panel';
import api from '../services/api';

const REGLAS_POR_DEFECTO = [
  { source: 'hostname', target: 'name', transform: '' },
  { source: 'ip_address', target: 'ip_address', transform: '' },
  { source: 'os', target: 'os', transform: '' },
  { source: 'ram_gb', target: 'ram_gb', transform: '' },
  { source: 'cpu', target: 'cpu', transform: '' },
];

const CI_TIPOS = [
  { value: 'workstation', label: 'Estación de trabajo' },
  { value: 'rack_element', label: 'Servidor (elemento de rack)' },
  { value: 'virt_vm', label: 'Máquina virtual' },
  { value: 'virt_hypervisor', label: 'Hipervisor' },
];

const MODOS_STORAGE = [
  { value: 'auto', label: 'Auto: local primero y Redfish solo si falta detalle' },
  { value: 'local', label: 'Solo local: nunca usar la BMC' },
  { value: 'redfish', label: 'Forzar Redfish/BMC cuando haya configuración' },
  { value: 'off', label: 'Apagado: sin enriquecimiento extra de storage' },
];

const REDFISH_VACIO = { host: '', user: '', password: '', system_id: '', verify_tls: false };

function etiquetaUmbral(valor) {
  if (valor < 60) return `${valor} s`;
  if (valor < 3600) return `${Math.round(valor / 60)} min`;
  return `${Math.round(valor / 3600)} h`;
}

export default function AgentMappingProfile({ companyId, embedded = true }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState('');

  /** Lo último que devolvió el backend: es a lo que vuelve «Descartar». */
  const [guardado, setGuardado] = useState(null);

  const [profileName, setProfileName] = useState('default');
  const [ciType, setCiType] = useState('workstation');
  const [threshold, setThreshold] = useState(300);
  const [storageMode, setStorageMode] = useState('auto');
  const [redfish, setRedfish] = useState(REDFISH_VACIO);
  const [rules, setRules] = useState(REGLAS_POR_DEFECTO);

  const aplicar = useCallback((p) => {
    const perfil = p || {};
    setProfileName(perfil.name ?? 'default');
    setCiType(perfil.default_ci_type ?? 'workstation');
    setThreshold(perfil.offline_threshold_seconds ?? 300);
    setStorageMode(perfil.storage_discovery_mode ?? 'auto');
    setRedfish({
      host: perfil.redfish?.host ?? '',
      user: perfil.redfish?.user ?? '',
      password: perfil.redfish?.password ?? '',
      system_id: perfil.redfish?.system_id ?? '',
      verify_tls: Boolean(perfil.redfish?.verify_tls),
    });
    setRules(perfil.rules?.length ? perfil.rules : REGLAS_POR_DEFECTO.map((r) => ({ ...r })));
  }, []);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getAgentMappingProfile(companyId);
      setGuardado(res.profile || null);
      aplicar(res.profile);
    } catch (e) {
      // Un 404 es lo normal la primera vez: todavía no hay perfil guardado.
      if (!String(e.message || '').includes('404') && !String(e.message || '').includes('not found')) {
        setError(e.message);
      }
      aplicar(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, aplicar]);

  useEffect(() => { cargar(); }, [cargar]);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 2500);
  };

  const cambiarRegla = (idx, campo, valor) => {
    setRules((prev) => {
      const copia = [...prev];
      copia[idx] = { ...copia[idx], [campo]: valor };
      return copia;
    });
  };

  const guardar = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.saveAgentMappingProfile(companyId, {
        name: profileName,
        default_ci_type: ciType,
        offline_threshold_seconds: threshold,
        storage_discovery_mode: storageMode,
        redfish,
        rules: rules.filter((r) => r.source && r.target),
      });
      mostrarAviso('Perfil guardado: el agente lo recogerá en su próximo sondeo');
      await cargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1rem 0' }}>
        <div className="list-hint">Selecciona una empresa para editar el mapeo del agente.</div>
      </div>
    );
  }

  const reglasValidas = rules.filter((r) => r.source && r.target).length;

  return (
    <div className={`list-page${embedded ? ' list-page--embedded' : ''}`}>
      {/* ══ Cabecera: de sección dentro de la pestaña, de página si va suelta ══ */}
      <div className="list-head">
        <div className="list-head-left">
          <ArrowsRightLeftIcon className="list-head-icon" />
          <div>
            {embedded
              ? <h2 className="list-section-title">Mapeo de campos</h2>
              : <h1 className="list-title">Mapeo de campos del agente</h1>}
            <p className={embedded ? 'list-section-subtitle' : 'list-subtitle'}>
              {reglasValidas} reglas válidas
              {rules.length !== reglasValidas ? ` · ${rules.length - reglasValidas} incompletas` : ''}
              {' · umbral offline '}{etiquetaUmbral(threshold)}
              {guardado?.updated_at ? ` · guardado ${new Date(guardado.updated_at).toLocaleString()}` : ''}
            </p>
          </div>
        </div>
        <div className="list-head-actions">
          {aviso && <span className="list-badge list-badge--on"><CheckIcon style={{ width: 13, height: 13, verticalAlign: '-2px' }} /> {aviso}</span>}
          <button
            type="button"
            className="list-btn-secondary"
            onClick={() => { aplicar(guardado); mostrarAviso('Cambios descartados'); }}
            disabled={saving}
            title="Vuelve a los valores guardados"
          >
            <ArrowPathIcon style={{ width: 15, height: 15 }} /> Descartar
          </button>
          <button
            type="button"
            className="list-btn-secondary"
            onClick={() => setRules(REGLAS_POR_DEFECTO.map((r) => ({ ...r })))}
            disabled={saving}
            title="Deja solo las cinco reglas de serie"
          >
            Reglas de serie
          </button>
          <button type="button" className="list-btn-primary" onClick={guardar} disabled={saving || loading}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      {error && <div className="list-badge list-badge--off" style={{ display: 'block', marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <div className="list-loading">
          <div className="spinner-border text-primary" aria-hidden="true" />
          <span>Cargando el perfil…</span>
        </div>
      ) : (
        <>
          <Panel
            title="Perfil del agente"
            hint="El agente descarga este perfil con su credencial: no hay que tocar el .env de los equipos."
          >
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label className="list-hint" style={{ flex: '1 1 200px' }}>
                Nombre del perfil
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                />
              </label>
              <label className="list-hint" style={{ flex: '1 1 220px' }}>
                Tipo de dispositivo por defecto
                <select
                  className="list-select"
                  style={{ width: '100%', marginTop: 4 }}
                  value={ciType}
                  onChange={(e) => setCiType(e.target.value)}
                >
                  {CI_TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
              <label className="list-hint" style={{ flex: '1 1 260px' }}>
                Umbral de offline: <strong>{etiquetaUmbral(threshold)}</strong>
                <input
                  type="range"
                  min={30}
                  max={3600}
                  step={30}
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                  style={{ width: '100%', marginTop: 8 }}
                />
                <span className="list-hint" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>30 s</span><span>30 min</span><span>1 h</span>
                </span>
              </label>
            </div>
            <p className="list-hint" style={{ margin: '10px 0 0' }}>
              Si un equipo lleva más de este tiempo sin latido, el CMDB lo marca como desconectado.
            </p>
          </Panel>

          <Panel
            title="Descubrimiento de storage"
            hint="Un solo agente decide si se queda en Windows, si sondea en local o si recurre a la BMC (Redfish) como respaldo."
            actions={<span className="list-badge">{storageMode}</span>}
          >
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label className="list-hint" style={{ flex: '1 1 280px' }}>
                Política de descubrimiento
                <select
                  className="list-select"
                  style={{ width: '100%', marginTop: 4 }}
                  value={storageMode}
                  onChange={(e) => setStorageMode(e.target.value)}
                >
                  {MODOS_STORAGE.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <div
                style={{
                  flex: '2 1 320px', padding: '10px 12px',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-toolbar)', fontSize: '0.8rem', color: 'var(--text-secondary)',
                }}
              >
                Con <strong>Auto</strong> el agente funciona con una sola instalación: solo recurre a la BMC
                si Windows no expone bien los miembros internos del RAID.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
              <label className="list-hint" style={{ flex: '1 1 240px' }}>
                Host o URL de la BMC
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={redfish.host}
                  onChange={(e) => setRedfish((p) => ({ ...p, host: e.target.value }))}
                  placeholder="https://ip-o-host-bmc"
                />
              </label>
              <label className="list-hint" style={{ flex: '0 1 140px' }}>
                System ID
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={redfish.system_id}
                  onChange={(e) => setRedfish((p) => ({ ...p, system_id: e.target.value }))}
                  placeholder="1"
                />
              </label>
              <label className="list-hint" style={{ flex: '1 1 180px' }}>
                Usuario
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={redfish.user}
                  onChange={(e) => setRedfish((p) => ({ ...p, user: e.target.value }))}
                  placeholder="usuario-bmc"
                />
              </label>
              <label className="list-hint" style={{ flex: '1 1 180px' }}>
                Contraseña
                <input
                  type="password"
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={redfish.password}
                  onChange={(e) => setRedfish((p) => ({ ...p, password: e.target.value }))}
                  placeholder="contraseña-bmc"
                />
              </label>
            </div>

            <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <input
                type="checkbox"
                checked={redfish.verify_tls}
                onChange={(e) => setRedfish((p) => ({ ...p, verify_tls: e.target.checked }))}
              />
              Verificar el certificado TLS de la BMC
            </label>

            <p className="list-hint" style={{ margin: '10px 0 0' }}>
              Si estos datos están aquí, el agente los descarga con su credencial: no hace falta editar el
              <code className="list-code" style={{ marginLeft: 4 }}>.env</code> de cada servidor.
            </p>
          </Panel>

          <Panel
            title="Reglas de mapeo de campos"
            hint="Campo del agente → campo del CMDB. La transformación admite expresiones simples, por ejemplo value.toUpperCase()."
            actions={(
              <button type="button" className="list-btn-secondary" onClick={() => setRules((p) => [...p, { source: '', target: '', transform: '' }])}>
                <PlusIcon style={{ width: 14, height: 14 }} /> Añadir regla
              </button>
            )}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rules.length === 0 && (
                <p className="list-hint" style={{ margin: 0 }}>
                  Sin reglas: el agente usará los nombres que trae por defecto.
                </p>
              )}
              {rules.map((rule, idx) => {
                const incompleta = !rule.source || !rule.target;
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap',
                      padding: '8px 10px',
                      border: `1px solid ${incompleta ? 'var(--amber-600)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-toolbar)',
                    }}
                    title={incompleta ? 'Le falta el campo del agente o el del CMDB: no se guardará' : undefined}
                  >
                    <label className="list-hint" style={{ flex: '1 1 160px' }}>
                      Campo del agente
                      <input
                        className="list-input"
                        style={{ width: '100%', marginTop: 4 }}
                        value={rule.source}
                        onChange={(e) => cambiarRegla(idx, 'source', e.target.value)}
                        placeholder="hostname"
                      />
                    </label>
                    <span className="list-hint" style={{ paddingBottom: 6 }}>→</span>
                    <label className="list-hint" style={{ flex: '1 1 160px' }}>
                      Campo del CMDB
                      <input
                        className="list-input"
                        style={{ width: '100%', marginTop: 4 }}
                        value={rule.target}
                        onChange={(e) => cambiarRegla(idx, 'target', e.target.value)}
                        placeholder="name"
                      />
                    </label>
                    <label className="list-hint" style={{ flex: '1 1 200px' }}>
                      Transformación (opcional)
                      <input
                        className="list-input"
                        style={{ width: '100%', marginTop: 4, fontFamily: 'var(--font-mono)' }}
                        value={rule.transform}
                        onChange={(e) => cambiarRegla(idx, 'transform', e.target.value)}
                        placeholder="value.trim()"
                      />
                    </label>
                    <button
                      type="button"
                      className="list-action-btn list-action-btn--danger"
                      onClick={() => setRules((prev) => prev.filter((_, i) => i !== idx))}
                      title="Quitar la regla"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                );
              })}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
